import { renderMathInElement } from './katexAutoRender';

// Preprocess math content to wrap undelimited math expressions (from mathRenderer)
function preprocessMathContent(content: string): string {
  if (!content) return '';

  // First, protect already-delimited math expressions
  const protectedSections: string[] = [];
  let protectedContent = content;

  const existingDelimiters = [
    /\$\$[\s\S]*?\$\$/g,
    /\\\([\s\S]*?\\\)/g,
    /\\\[[\s\S]*?\\\]/g,
  ];

  existingDelimiters.forEach((delimiter) => {
    protectedContent = protectedContent.replace(delimiter, (match) => {
      const index = protectedSections.length;
      protectedSections.push(match);
      return `__PROTECTED_MATH_${index}__`;
    });
  });

  // Clean up whitespace and normalize content - be more aggressive with line breaks
  protectedContent = protectedContent.replace(/\s+/g, ' ').trim();

  // Remove duplicated symbols that appear side by side - be more aggressive
  protectedContent = protectedContent.replace(/π\s*π/g, 'π');
  protectedContent = protectedContent.replace(/÷\s*÷/g, '÷');

  // Handle pi symbols early to prevent duplication issues
  protectedContent = protectedContent.replace(/\bπ\b/g, '\\pi');

  // CRITICAL: Handle fractions FIRST before any other rules can create nested delimiters
  // This prevents the nested \( \) issue that's causing KaTeX parse errors
  protectedContent = protectedContent.replace(
    /\\frac\{([^{}]*(?:\{[^}]*\}[^{}]*)*)\}\{([^{}]*(?:\{[^}]*\}[^{}]*)*)\}/g,
    (match, numerator, denominator) => {
      // Aggressively clean any existing \( \) from inside the fraction parts
      let cleanNum = numerator
        .replace(/\\\(|\\\)/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      let cleanDen = denominator
        .replace(/\\\(|\\\)/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      // Protect this fraction immediately to prevent further processing
      const index = protectedSections.length;
      const cleanFraction = `\\(\\frac{${cleanNum}}{${cleanDen}}\\)`;
      protectedSections.push(cleanFraction);
      return `__PROTECTED_MATH_${index}__`;
    },
  );

  // Process other math expressions - but now fractions are protected

  // 1. Handle equations with pi and mathematical operations (most specific)
  protectedContent = protectedContent.replace(
    /([A-Z])\s*=\s*(\\pi\s*\\cdot\s*\d+\s*\\cdot\s*\d+)/g,
    (match, variable, expression) => `\\(${variable}=${expression}\\)`,
  );

  // 2. Handle equations with just pi
  protectedContent = protectedContent.replace(
    /([A-Z])\s*=\s*_*\s*(\\pi)\s*$/g,
    (match, variable, pi) => `\\(${variable}=${pi}\\)`,
  );

  // 3. Handle \cdot operators specifically (before other processing)
  protectedContent = protectedContent.replace(/\\cdot/g, '\\(\\cdot\\)');

  // 4. Handle very specific scientific notation patterns with line breaks
  protectedContent = protectedContent.replace(
    /\(\s*(\d+(?:\.\d+)?)\s*×\s*10\s*(\d+)\s*\1×10\s*\2\s*\)/g,
    (match, coefficient, exponent) =>
      `\\((${coefficient}\\times 10^{${exponent}})\\)`,
  );

  // 5. Handle scientific notation with split exponents like "10 1 0" = "10^10"
  protectedContent = protectedContent.replace(
    /\(\s*(\d+(?:\.\d+)?)\s*×\s*10\s*(\d+)\s*(\d+)\s*\1×10\s*\2\s*\3\s*\)/g,
    (match, coefficient, exp1, exp2) =>
      `\\((${coefficient}\\times 10^{${exp1}${exp2}})\\)`,
  );

  // 6. Handle scientific notation in parentheses with line breaks (simpler cases)
  protectedContent = protectedContent.replace(
    /\(\s*(\d+(?:\.\d+)?)\s*×\s*10\s*(\d+)\s*\)/g,
    (match, coefficient, exponent) =>
      `\\((${coefficient}\\times 10^{${exponent}})\\)`,
  );

  // 7. Handle scientific notation without parentheses but with line breaks
  protectedContent = protectedContent.replace(
    /(\d+(?:\.\d+)?)\s*×\s*10\s*(\d+)/g,
    (match, coefficient, exponent) =>
      `\\(${coefficient}\\times 10^{${exponent}}\\)`,
  );

  // 8. Complete scientific notation expressions
  protectedContent = protectedContent.replace(
    /\((\d+(?:\.\d+)?)\s+(\d+\^(?:\{[^}]+\}|\d+))\)/g,
    (match, coefficient, power) => `\\((${coefficient}\\times ${power})\\)`,
  );

  // 9. Scientific notation without parentheses
  protectedContent = protectedContent.replace(
    /(\d+(?:\.\d+)?)\s+(10\^(?:\{[^}]+\}|\d+))/g,
    (match, coefficient, power) => `\\(${coefficient}\\times ${power}\\)`,
  );

  // 10. Scientific notation with \times but missing space
  protectedContent = protectedContent.replace(
    /(\d+(?:\.\d+)?)\\times(\d+\^(?:\{[^}]+\}|\d+))/g,
    (match, coefficient, power) => `\\(${coefficient}\\times ${power}\\)`,
  );

  // 11. Overline notation
  protectedContent = protectedContent.replace(
    /(\d+\.?)\\overline\{([^}]*)\}/g,
    (match, number, overlined) => `\\(${number}\\overline{${overlined}}\\)`,
  );

  // 12. Roots with indices
  protectedContent = protectedContent.replace(
    /\\sqrt\[([^\]]*)\]\{([^}]*)\}/g,
    (match, index, content) => `\\(\\sqrt[${index}]{${content}}\\)`,
  );

  // 13. Simple roots
  protectedContent = protectedContent.replace(
    /\\sqrt\{([^}]*)\}/g,
    (match, content) => `\\(\\sqrt{${content}}\\)`,
  );

  // 14. Division fractions (simple a/b format)
  protectedContent = protectedContent.replace(
    /\b([a-zA-Z0-9]+)\s*\/\s*([a-zA-Z0-9]+)\b/g,
    (match, num, den) => `\\(\\frac{${num}}{${den}}\\)`,
  );

  // 15. Exponents with braces or superscript
  protectedContent = protectedContent.replace(
    /(\d+)\^?\{([^}]+)\}/g,
    (match, base, exponent) => `\\(${base}^{${exponent}}\\)`,
  );

  // 16. Greek letters (standalone)
  protectedContent = protectedContent.replace(
    /\\(alpha|beta|gamma|delta|epsilon|theta|lambda|mu|pi|sigma|phi|psi|omega)\b/g,
    (match, letter) => `\\(\\${letter}\\)`,
  );

  // 17. Math operators (standalone) - but \cdot is already handled above
  protectedContent = protectedContent.replace(
    /\\(div)\b/g,
    (match, op) => `\\(\\${op}\\)`,
  );

  // 18. Math functions
  protectedContent = protectedContent.replace(
    /\\(sum|int|lim|sin|cos|tan|log|ln)\b/g,
    (match, func) => `\\(\\${func}\\)`,
  );

  // 19. Simple equations with special characters (be very selective)
  // First, exclude cases where both sides are common English words by wrapping in a protected span
  const commonWords = ['to', 'and', 'or', 'the', 'in', 'on', 'at', 'by', 'for'];
  const wordPattern = new RegExp(
    `\\b(${commonWords.join('|')})\\s*=\\s*(${commonWords.join('|')})\\b`,
    'i',
  );
  protectedContent = protectedContent.replace(
    wordPattern,
    '<span class="no-katex">$1 = $2</span>',
  );

  // Then handle actual mathematical expressions
  protectedContent = protectedContent.replace(
    /\b([A-Z])\s*=\s*([^.]*[×÷\\][^.]*)/g,
    (match, variable, expression) => `\\(${variable}=${expression}\\)`,
  );

  // Final cleanup: Remove any remaining nested delimiters that might have been created
  protectedContent = protectedContent.replace(/\\\(\s*\\\(/g, '\\(');
  protectedContent = protectedContent.replace(/\\\)\s*\\\)/g, '\\)');

  // Restore protected sections
  protectedSections.forEach((section, index) => {
    protectedContent = protectedContent.replace(
      `__PROTECTED_MATH_${index}__`,
      section,
    );
  });

  return protectedContent;
}

import type { Directive } from 'vue';

export const vKatex: Directive = {
  mounted(el: HTMLElement, binding) {
    // Accepts either string or { html: string }
    let html =
      typeof binding.value === 'string' ? binding.value : binding.value?.html;
    if (!html) return;
    // Preprocess math content
    html = preprocessMathContent(html);
    el.innerHTML = html;
    if (typeof window !== 'undefined' && window.katex) {
      try {
        renderMathInElement(el, {
          delimiters: [
            { left: '$$', right: '$$', display: true },
            { left: '\\(', right: '\\)', display: false },
            { left: '\\[', right: '\\]', display: true },
          ],
          ignoredTags: [
            'script',
            'noscript',
            'style',
            'textarea',
            'pre',
            'code',
            'option',
          ],
          ignoredClasses: ['no-katex'],
          errorCallback: (msg: string, err: any) => {
            console.warn('KaTeX auto-render error:', msg, err);
          },
        });
      } catch (error) {
        console.warn('Error processing math equations:', error);
      }
    }
  },
  updated(el: HTMLElement, binding) {
    // Same as mounted
    let html =
      typeof binding.value === 'string' ? binding.value : binding.value?.html;
    if (!html) return;
    html = preprocessMathContent(html);
    el.innerHTML = html;
    if (typeof window !== 'undefined' && window.katex) {
      try {
        renderMathInElement(el, {
          delimiters: [
            { left: '$$', right: '$$', display: true },
            { left: '\\(', right: '\\)', display: false },
            { left: '\\[', right: '\\]', display: true },
          ],
          ignoredTags: [
            'script',
            'noscript',
            'style',
            'textarea',
            'pre',
            'code',
            'option',
          ],
          ignoredClasses: ['no-katex'],
          errorCallback: (msg: string, err: any) => {
            console.warn('KaTeX auto-render error:', msg, err);
          },
        });
      } catch (error) {
        console.warn('Error processing math equations:', error);
      }
    }
  },
};

export default vKatex;
